import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const CATEGORY_API = `${API_BASE_URL}/api/categories`;

const initialForm = {
  name: "",
  slug: "",
  description: "",
  isActive: true,
};

function CategoriesManager() {
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");

  const fetchCategories = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(CATEGORY_API, {
        params: { search },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCategories(data?.categories || data || []);
    } catch (err) {
      console.log(err);
      alert("Unable to fetch categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [search]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);

    if (file) {
      // Creates a temporary local URL for the selected file preview
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);
    } else {
      setPreviewImage("");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setImageFile(null);
    setPreviewImage("");
    setShowModal(true);
  };

  const openEdit = (category) => {
    setEditingId(category._id || category.id);
    setForm({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      isActive: category.isActive !== false,
    });
    setImageFile(null);
    // Directly use the Cloudinary URL from the database
    setPreviewImage(category.image || "");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
    setImageFile(null);
    setPreviewImage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("slug", form.slug);
      formData.append("description", form.description);
      formData.append("isActive", form.isActive);

      if (imageFile) {
        formData.append("image", imageFile);
      }

      if (editingId) {
        await axios.put(`${CATEGORY_API}/${editingId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Category updated successfully.");
      } else {
        await axios.post(CATEGORY_API, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Category created successfully.");
      }

      closeModal();
      fetchCategories();
    } catch (err) {
      console.log(err);
      alert("Unable to save category.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Delete this category?");
    if (!confirmed) return;

    try {
      await axios.delete(`${CATEGORY_API}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      alert("Category deleted successfully.");
      fetchCategories();
    } catch (err) {
      console.log(err);
      alert("Unable to delete category.");
    }
  };

  return (
    <div className="manager-section">
      <div className="manager-topbar">
        <div>
          <h2>Categories</h2>
          <p>Create, update, and remove categories used in the product form.</p>
        </div>

        <button className="theme-btn" onClick={openCreate}>
          Add Category
        </button>
      </div>

      <div className="manager-tools">
        <div className="manager-search-wrap">
          <input
            type="text"
            className="manager-search"
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="manager-table-wrap">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading categories...</td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan="6">
                  <div className="empty-state">No categories found.</div>
                </td>
              </tr>
            ) : (
              categories.map((item) => (
                <tr key={item._id || item.id}>
                  <td>
                    {item.image && item.image.startsWith("http") ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: "56px",
                          height: "56px",
                          objectFit: "cover",
                          borderRadius: "12px",
                          border: "1px solid #eee",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#888" }}>No Image</span>
                    )}
                  </td>
                  <td>{item.name}</td>
                  <td>{item.slug}</td>
                  <td>{item.description || "-"}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        item.isActive ? "status-active" : "status-inactive"
                      }`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="outline-btn" onClick={() => openEdit(item)}>
                        Edit
                      </button>
                      <button
                        className="danger-btn"
                        onClick={() => handleDelete(item._id || item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card small">
            <div className="modal-head">
              <div>
                <h3>{editingId ? "Edit Category" : "Add Category"}</h3>
                <p>Maintain product categories used in catalog management.</p>
              </div>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    className="manager-input"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Slug</label>
                  <input
                    className="manager-input"
                    name="slug"
                    value={form.slug}
                    onChange={handleChange}
                    placeholder="mens-fashion"
                    required
                  />
                </div>

                <div className="form-group full">
                  <label>Description</label>
                  <textarea
                    className="manager-textarea"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group full">
                  <label>Category Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="manager-input"
                    onChange={handleImageChange}
                  />
                </div>

                {previewImage && (
                  <div className="form-group full">
                    <div style={{ marginTop: "6px" }}>
                      <img
                        src={previewImage}
                        alt="Preview"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "16px",
                          border: "1px solid #e5e5e5",
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="form-group full">
                  <div className="checkbox-row">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={form.isActive}
                        onChange={handleChange}
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="outline-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="theme-btn" disabled={submitting}>
                  {submitting
                    ? "Saving..."
                    : editingId
                    ? "Update Category"
                    : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoriesManager;